import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import { UNAUTHORIZED_EVENT } from '../api/axiosInterceptor';
import { ensureMsalInitialized, getRedirectResponse, loginRequest, msalInstance } from '../auth/msalConfig';
import type { User, AuthContextType } from '../types';

// @preserved_behavior: Root AuthContext is kept internally to prevent leaking non-component exports.
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    bootstrap();
  }, []);

  // A 401 on a non-auth request (expired/absent session) fires this event from
  // the axios interceptor. Clearing `user` trips ProtectedRoute's redirect to
  // /login, and clearing the query cache prevents the previous session's data
  // from leaking. The interceptor skips `/auth/*`, so this never loops with the
  // login / getMe flow.
  useEffect(() => {
    function handleUnauthorized() {
      setUser(null);
      queryClient.clear();
    }
    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
  }, [queryClient]);

  const bootstrap = async () => {
    try {
      const result = await getRedirectResponse();
      if (result && result.idToken) {
        msalInstance.setActiveAccount(result.account);
        const response = await authApi.loginWithMicrosoft(result.idToken);
        setUser(response.user);
        setLoading(false);
        return;
      }
    } catch (err) {
      console.error('Microsoft redirect handling failed', err);
    }
    await checkAuth();
  };

  const checkAuth = async () => {
    try {
      const userData = await authApi.getMe();
      setUser(userData);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    const response = await authApi.login({ email, password });
    setUser(response.user);
  };

  const loginWithMicrosoft = async () => {
    await ensureMsalInitialized();
    await msalInstance.loginRedirect(loginRequest);
    // loginRedirect navigates away; the promise above never resolves in this page.
  };

  const logout = async () => {
    // Backend logout must succeed first - the session cookie is HttpOnly,
    // so only the backend's Set-Cookie can clear it. If this throws, leave
    // the UI as-is and let AppNavbar's catch handle the error; clearing
    // local state while the real session is still alive would mislead
    // the user about whether they are actually logged out.
    await authApi.logout();

    // Best-effort MSAL cache cleanup. The real logout already happened
    // (backend cookie cleared), so a failure here is non-fatal - leftover
    // local MSAL state is a minor inconvenience, not a security issue.
    // Deliberately do NOT call msalInstance.logoutRedirect: Microsoft's
    // tenant logout endpoint forces a "Pick an account" picker that we
    // cannot suppress, and selecting the wrong entry signs that account
    // out of every other Microsoft service. The shared-computer security
    // goal is met instead by prompt: 'login' on the next sign-in, which
    // forces a fresh Microsoft re-authentication regardless of cached
    // MS session.
    try {
      await msalInstance.clearCache();
    } catch (error) {
      console.error('Failed to clear MSAL cache:', error);
    }

    setUser(null);
    // Drop all cached server state so the next user (e.g. on a shared machine)
    // can never see the previous user's data from the React Query cache.
    queryClient.clear();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithMicrosoft, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
